import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, EditorState, FORMAT_TEXT_COMMAND, FORMAT_ELEMENT_COMMAND } from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createParagraphNode } from 'lexical';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, REMOVE_LIST_COMMAND } from '@lexical/list';
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Quote,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
  Link as LinkIcon
} from 'lucide-react';

const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
  },
  list: {
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
  image: 'editor-image',
  link: 'editor-link',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    overflowed: 'editor-text-overflowed',
    hashtag: 'editor-text-hashtag',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
    code: 'editor-text-code',
  },
  code: 'editor-code',
  codeHighlight: {
    atrule: 'editor-tokenAttr',
    attr: 'editor-tokenAttr',
    boolean: 'editor-tokenProperty',
    builtin: 'editor-tokenSelector',
    cdata: 'editor-tokenComment',
    char: 'editor-tokenSelector',
    class: 'editor-tokenFunction',
    'class-name': 'editor-tokenFunction',
    comment: 'editor-tokenComment',
    constant: 'editor-tokenProperty',
    deleted: 'editor-tokenProperty',
    doctype: 'editor-tokenComment',
    entity: 'editor-tokenOperator',
    function: 'editor-tokenFunction',
    important: 'editor-tokenVariable',
    inserted: 'editor-tokenSelector',
    keyword: 'editor-tokenAttr',
    namespace: 'editor-tokenVariable',
    number: 'editor-tokenProperty',
    operator: 'editor-tokenOperator',
    prolog: 'editor-tokenComment',
    property: 'editor-tokenProperty',
    punctuation: 'editor-tokenPunctuation',
    regex: 'editor-tokenVariable',
    selector: 'editor-tokenSelector',
    string: 'editor-tokenSelector',
    symbol: 'editor-tokenProperty',
    tag: 'editor-tokenProperty',
    url: 'editor-tokenOperator',
    variable: 'editor-tokenVariable',
  },
};

function onError(error: Error) {
  console.error(error);
}

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = React.useState(false);
  const [canRedo, setCanRedo] = React.useState(false);

  React.useEffect(() => {
    return editor.registerCommand(
      'CAN_UNDO_COMMAND' as any,
      (payload: boolean) => {
        setCanUndo(payload);
        return false;
      },
      4
    );
  }, [editor]);

  React.useEffect(() => {
    return editor.registerCommand(
      'CAN_REDO_COMMAND' as any,
      (payload: boolean) => {
        setCanRedo(payload);
        return false;
      },
      4
    );
  }, [editor]);

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: 'h1' | 'h2' | 'h3') => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      editor.dispatchCommand('TOGGLE_LINK_COMMAND' as any, url);
    }
  };

  return (
    <div className="toolbar" style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '4px',
      padding: '8px',
      borderBottom: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Undo/Redo */}
      <button
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand('UNDO_COMMAND' as any, undefined)}
        className="toolbar-item"
        type="button"
        title="Undo (Ctrl+Z)"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: canUndo ? 'pointer' : 'not-allowed',
          opacity: canUndo ? 1 : 0.5,
        }}
      >
        <Undo size={18} />
      </button>
      <button
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand('REDO_COMMAND' as any, undefined)}
        className="toolbar-item"
        type="button"
        title="Redo (Ctrl+Y)"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: canRedo ? 'pointer' : 'not-allowed',
          opacity: canRedo ? 1 : 0.5,
        }}
      >
        <Redo size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Text Formatting */}
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        className="toolbar-item"
        type="button"
        title="Bold (Ctrl+B)"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Bold size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        className="toolbar-item"
        type="button"
        title="Italic (Ctrl+I)"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Italic size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        className="toolbar-item"
        type="button"
        title="Underline (Ctrl+U)"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Underline size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        className="toolbar-item"
        type="button"
        title="Strikethrough"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Strikethrough size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
        className="toolbar-item"
        type="button"
        title="Insert Code"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Code size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Headings & Blocks */}
      <button
        onClick={() => formatHeading('h1')}
        className="toolbar-item"
        type="button"
        title="Heading 1"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Heading1 size={18} />
      </button>
      <button
        onClick={() => formatHeading('h2')}
        className="toolbar-item"
        type="button"
        title="Heading 2"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Heading2 size={18} />
      </button>
      <button
        onClick={() => formatHeading('h3')}
        className="toolbar-item"
        type="button"
        title="Heading 3"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Heading3 size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Lists */}
      <button
        onClick={() => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)}
        className="toolbar-item"
        type="button"
        title="Bullet List"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <List size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)}
        className="toolbar-item"
        type="button"
        title="Numbered List"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <ListOrdered size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Quote */}
      <button
        onClick={formatQuote}
        className="toolbar-item"
        type="button"
        title="Quote"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <Quote size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Alignment */}
      <button
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
        className="toolbar-item"
        type="button"
        title="Align Left"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <AlignLeft size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
        className="toolbar-item"
        type="button"
        title="Align Center"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <AlignCenter size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
        className="toolbar-item"
        type="button"
        title="Align Right"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <AlignRight size={18} />
      </button>
      <button
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
        className="toolbar-item"
        type="button"
        title="Justify"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <AlignJustify size={18} />
      </button>

      <div style={{ width: '1px', height: '32px', backgroundColor: '#d1d5db', margin: '0 4px' }} />

      {/* Link */}
      <button
        onClick={insertLink}
        className="toolbar-item"
        type="button"
        title="Insert Link"
        style={{
          padding: '6px 10px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          backgroundColor: 'white',
          cursor: 'pointer',
        }}
      >
        <LinkIcon size={18} />
      </button>
    </div>
  );
}

interface RichTextEditorProps {
  initialValue?: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichTextEditor({ initialValue = '', onChange, placeholder = 'Start writing...' }: RichTextEditorProps) {
  const initialConfig = {
    namespace: 'BlogEditor',
    theme,
    onError,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
    ],
  };

  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const htmlString = root.getTextContent(); // You can implement HTML serialization
      onChange(htmlString);
    });
  };

  return (
    <div className="editor-container" style={{
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      backgroundColor: 'white',
      minHeight: '500px',
      overflow: 'hidden',
    }}>
      <LexicalComposer initialConfig={initialConfig}>
        <ToolbarPlugin />
        <div style={{ position: 'relative', backgroundColor: 'white' }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className="editor-input"
                style={{
                  minHeight: '450px',
                  padding: '20px',
                  outline: 'none',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: '#1f2937',
                }}
              />
            }
            placeholder={
              <div
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  color: '#9ca3af',
                  fontSize: '16px',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              >
                {placeholder}
              </div>
            }
            ErrorBoundary={(props: any) => <div>{props.children}</div>}
          />
          <OnChangePlugin onChange={handleChange} />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        </div>
      </LexicalComposer>

      <style>{`
        .editor-paragraph {
          margin: 0 0 12px 0;
        }
        .editor-heading-h1 {
          font-size: 32px;
          font-weight: 700;
          margin: 24px 0 16px 0;
          line-height: 1.2;
        }
        .editor-heading-h2 {
          font-size: 24px;
          font-weight: 700;
          margin: 20px 0 12px 0;
          line-height: 1.3;
        }
        .editor-heading-h3 {
          font-size: 20px;
          font-weight: 600;
          margin: 16px 0 8px 0;
          line-height: 1.4;
        }
        .editor-quote {
          margin: 16px 0;
          padding-left: 16px;
          border-left: 4px solid #3b82f6;
          color: #6b7280;
          font-style: italic;
        }
        .editor-list-ol,
        .editor-list-ul {
          padding-left: 24px;
          margin: 12px 0;
        }
        .editor-listitem {
          margin: 4px 0;
        }
        .editor-text-bold {
          font-weight: bold;
        }
        .editor-text-italic {
          font-style: italic;
        }
        .editor-text-underline {
          text-decoration: underline;
        }
        .editor-text-strikethrough {
          text-decoration: line-through;
        }
        .editor-text-code {
          background-color: #f3f4f6;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
        }
        .editor-link {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .editor-link:hover {
          color: #2563eb;
        }
        .editor-code {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 16px;
          border-radius: 8px;
          margin: 16px 0;
          overflow-x: auto;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          line-height: 1.5;
        }
      `}</style>
    </div>
  );
}
